import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, Braces, Brain, Copy, Check, Eye, EyeOff, Terminal } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  sessionLabel: string | null
  sessionId: string
  digitalWorkerId: string
  isConversation: boolean
  role: string
  showThinking: boolean
  showToolCalls: boolean
  showToolResults: boolean
  onNavigate: (overrides: Record<string, string | number | undefined>) => void
  currentPage: number
}

export default function SessionHeader({
  sessionLabel, sessionId, digitalWorkerId, isConversation, role,
  showThinking, showToolCalls, showToolResults, onNavigate, currentPage,
}: Props) {
  const [copied, setCopied] = useState(false)

  const handleCopySessionId = () => {
    navigator.clipboard.writeText(sessionId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleRoleFilter = (value: string) => {
    onNavigate({ role: value || undefined, page: 1 })
  }

  const toggleViewMode = (mode: string) => {
    onNavigate({ view_mode: mode, page: 1, role: mode === 'conversation' ? undefined : role || undefined })
  }

  return (
    <div className="flex items-center gap-3 mb-2">
      <Link
        href={`/cc/telemetry/digital_workers/${digitalWorkerId}`}
        className="text-muted-foreground hover:text-muted-foreground transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <h2 className="text-2xl font-bold text-foreground flex-1">
        {sessionLabel || sessionId.slice(0, 8)}
      </h2>
      <div className="flex items-center gap-1">
        <code className="font-mono text-xs text-muted-foreground hidden sm:inline">{sessionId.slice(0, 8)}</code>
        <button
          onClick={handleCopySessionId}
          className="text-muted-foreground hover:text-muted-foreground transition-colors"
          title="Copiar session ID"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      {!isConversation && (
        <select
          value={role}
          onChange={(e) => handleRoleFilter(e.target.value)}
          className="text-sm border border-border rounded-lg px-2 py-1.5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">Todos</option>
          <option value="user">Usuário</option>
          <option value="assistant">Assistente</option>
          <option value="toolResult">Tool Result</option>
          <option value="system">Sistema</option>
        </select>
      )}
      <button
        onClick={() => onNavigate({ show_thinking: showThinking ? 'false' : undefined, page: currentPage })}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors',
          showThinking
            ? 'border-border text-foreground bg-background hover:bg-accent'
            : 'border-border text-muted-foreground bg-muted hover:bg-accent'
        )}
        title={showThinking ? 'Ocultar pensamento' : 'Mostrar pensamento'}
      >
        {showThinking ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        <Brain className="h-3.5 w-3.5" />
        <span className="hidden lg:inline">Pensamento</span>
      </button>
      <button
        onClick={() => onNavigate({ show_tool_calls: showToolCalls ? 'false' : undefined, page: currentPage })}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors',
          showToolCalls
            ? 'border-border text-foreground bg-background hover:bg-accent'
            : 'border-border text-muted-foreground bg-muted hover:bg-accent'
        )}
        title={showToolCalls ? 'Ocultar tool calls' : 'Mostrar tool calls'}
      >
        {showToolCalls ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        <Terminal className="h-3.5 w-3.5" />
        <span className="hidden lg:inline">Tools</span>
      </button>
      {isConversation && (
        <button
          onClick={() => onNavigate({ show_tool_results: showToolResults ? undefined : 'true', page: 1 })}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors',
            showToolResults
              ? 'border-border text-foreground bg-background hover:bg-accent'
              : 'border-border text-muted-foreground bg-muted hover:bg-accent'
          )}
          title={showToolResults ? 'Ocultar resultados das tools' : 'Mostrar resultados das tools'}
        >
          {showToolResults ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          <Braces className="h-3.5 w-3.5" />
          <span className="hidden lg:inline">Resultados</span>
        </button>
      )}
      <div className="bg-accent p-0.5 rounded-lg inline-flex">
        <button
          onClick={() => toggleViewMode('conversation')}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
            isConversation
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-accent-foreground'
          )}
        >
          Conversa
        </button>
        <button
          onClick={() => toggleViewMode('all')}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
            !isConversation
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-accent-foreground'
          )}
        >
          Todos os eventos
        </button>
      </div>
    </div>
  )
}
